import React, { createContext, useContext, useEffect, useState } from 'react';
import { ConsentCategories, ConsentContextType, FullConsentState, StoredConsentRecord } from './types';
import { applyConsentEffects, getStoredConsent, saveStoredConsent } from './utils';

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [storedRecord, setStoredRecord] = useState<StoredConsentRecord | null>(() => getStoredConsent());
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(() => !getStoredConsent());

  const categories: FullConsentState = {
    strictlyNecessary: true,
    analytics: storedRecord ? storedRecord.categories.analytics : false,
    performanceDiagnostics: storedRecord ? storedRecord.categories.performanceDiagnostics : false
  };

  const hasConsented = Boolean(storedRecord);

  // Apply consent side-effects whenever storedRecord changes or on initial load
  useEffect(() => {
    if (storedRecord) {
      applyConsentEffects(storedRecord.categories);
    } else {
      applyConsentEffects({ analytics: false, performanceDiagnostics: false });
    }
  }, [storedRecord]);

  const commitConsent = (newCategories: ConsentCategories) => {
    const record = saveStoredConsent(newCategories);
    setStoredRecord(record);
    setIsDialogOpen(false);
  };

  const acceptAll = () => {
    commitConsent({ analytics: true, performanceDiagnostics: true });
  };

  const rejectAll = () => {
    commitConsent({ analytics: false, performanceDiagnostics: false });
  };

  const updateCategories = (newCategories: Partial<ConsentCategories>) => {
    const updated: ConsentCategories = {
      analytics: newCategories.analytics ?? categories.analytics,
      performanceDiagnostics: newCategories.performanceDiagnostics ?? categories.performanceDiagnostics
    };
    commitConsent(updated);
  };

  const openConsentSettings = () => setIsDialogOpen(true);
  const closeConsentSettings = () => {
    if (!hasConsented) {
      rejectAll();
    } else {
      setIsDialogOpen(false);
    }
  };

  return (
    <ConsentContext.Provider
      value={{
        categories,
        hasConsented,
        isDialogOpen,
        storedRecord,
        acceptAll,
        rejectAll,
        updateCategories,
        openConsentSettings,
        closeConsentSettings
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextType => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
